import style from '../styles/App.module.scss';
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';

const MIN_HEIGHT_FOR_MAX_SIZE = 215;
const MIN_HEIGHT_FOR_MID_SIZE = 180;
const MIN_HEIGHT_FOR_BRIDGE = 150;
const MIN_WIDTH_FOR_MAX_SIZE = 500;
const MIN_WIDTH_FOR_MID_SIZE = 350;
const MIN_WIDTH_FOR_BRIDGE = 350;

export const useAspectRatioClass = <T extends HTMLElement>(): [MutableRefObject<T>, string, () => void] => {
    const appRef = useRef<T>(null);
    const [refresh, setRefresh] = useState(false);
    
    const reevaluateSize = () => setRefresh(true);
    
    useEffect(() => {
        window.addEventListener('resize', reevaluateSize);
        
        return () => {
            window.removeEventListener('resize', reevaluateSize);
        };
    }, []);
    
    const aspectRatioClass = useMemo(() => {
        if (!appRef.current) {
            return style.defaultRatio;
        }
        if (refresh) setRefresh(false);
        // Set aspect ratio of the box based on its parent, because it isn't a constant
        const parent = appRef.current.offsetParent;
        const box = parent.getBoundingClientRect();
        if (box.height < MIN_HEIGHT_FOR_BRIDGE || box.width < MIN_WIDTH_FOR_BRIDGE) {
            return style.littleBoxRatio;
        }
        if (box.height < MIN_HEIGHT_FOR_MID_SIZE || box.width < MIN_WIDTH_FOR_MID_SIZE) {
            return style.bridgeRatio;
        }
        if (box.height < MIN_HEIGHT_FOR_MAX_SIZE || box.width < MIN_WIDTH_FOR_MAX_SIZE) {
            return style.middlingRatio;
        }
        return style.defaultRatio;
    }, [appRef.current, refresh]);
    
    return [appRef, aspectRatioClass, reevaluateSize];
};